import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import styled, { ThemeProvider } from 'styled-components';
import css from '@styled-system/css';
import { ThumbsUp, Warning } from 'react-dripicons';
import { parseCookies, setCookie } from 'nookies';

import { theme } from 'utils/theme.v2';
import Box from 'humanity/primitives/box';
import Container from 'humanity/primitives/container';
import Grid from 'humanity/primitives/grid';
import Section from 'humanity/primitives/section';
import {
  guideMastheadDefaultProps,
  guideMastheadProps,
} from 'humanity/sections/guideMasthead/guideMasthead.types.js';
import Heading from 'humanity/primitives/heading';
import Text from 'humanity/primitives/text';
import SimpleRichText from 'humanity/components/simpleRichText';
import Image from 'humanity/primitives/image';
import { constrainImage } from 'utils/constrainImage';
import ContentfulForm from 'humanity/components/contentfulForm';
import { useHubspotForm } from 'hooks/useHubspotForm';
import Flex from 'humanity/primitives/flex';
import useDownloadFile from 'hooks/useDownloadFile';
import Button from 'humanity/primitives/button';
import Loading from 'humanity/components/loading';
import { track } from 'utils/analytics';

const HeadingWrapper = styled(Box)(
  css({
    gridColumn: ['1 / span 12', null, null, '1 / span 6'],
    gridRow: '1 / auto',
  })
);

const ParagraphWrapper = styled(Box)(
  css({
    gridColumn: ['1 / span 12', null, null, '1 / span 6'],
    gridRow: ['3 / auto', null, null, '2 / auto'],
  })
);

const ResourceWrapper = styled(Box)(
  css({
    gridColumn: ['1 / span 12', null, null, '8 / span 5'],
    gridRow: ['2 / auto', null, null, '1 / span 3'],
  })
);

const ResourceCard = styled(Box)(
  css({
    p: 5,
    bg: 'blue10',
    // why oh why can't we stick to design system values :(
    borderRadius: 'sm',
  })
);

const ImageWrapper = styled(Box)(
  css({
    borderRadius: 'sm',
    lineHeight: 0,
    overflow: 'hidden',
  })
);

const formCardBaseStyles = {
  gap: 3,
  width: '100%',
  px: 3,
  py: 2,
  borderRadius: 'sm',
};

const FormCardIconWrapper = styled(Flex)(
  css({
    flexShrink: 0,
    justifyContent: 'center',
    alignItems: 'flex-start',
    pt: 1,
  })
);

const FormSuccessCard = styled(Flex)(
  css({
    ...formCardBaseStyles,
    bg: 'teal20',
  })
);

const FormErrorCard = styled(Flex)(
  css({
    ...formCardBaseStyles,
    bg: '#fcd9e2',
    color: 'redError',
  })
);

const STATUS = {
  IDLE: 'IDLE',
  LOADING: 'LOADING',
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
};

const GUIDE_COOKIE = 'hin_guides';

const GuideMasthead = ({
  eyebrow,
  heading,
  paragraph,
  image,
  form,
  formSuccessCopy,
  downloadHeading,
  downloadParagraph,
  downloadUrl,
  downloadTitle,
  downloadCtaLabel,
}) => {
  const [formStatus, setFormStatus] = useState(STATUS.IDLE);
  const { submitForm } = useHubspotForm(
    form?.hubspotFormId,
    form?.hubspotSandboxFormId,
    form?.salesforceCampaignId,
    form?.formType
  );
  const { status: downloadStatus, downloadFile } = useDownloadFile(
    downloadUrl,
    downloadTitle,
    'application/pdf'
  );
  const {
    query: { ty },
  } = useRouter();

  useEffect(() => {
    // check for the presence of a "ty" query param or cookie to bypass form fill
    if (ty === 'true') {
      setFormStatus(STATUS.SUCCESS);
      setCookie(null, GUIDE_COOKIE, true, { maxAge: 30 * 24 * 60 * 60 });
    } else {
      const cookies = parseCookies();

      if (cookies[GUIDE_COOKIE]) {
        setFormStatus(STATUS.SUCCESS);
      }
    }
  }, [ty]);

  const { width: imageWidth, height: imageHeight } = constrainImage(
    image.width,
    image.height,
    420
  );

  const handleSubmit = async (values) => {
    setFormStatus(STATUS.LOADING);
    try {
      await submitForm(values);
      setFormStatus(STATUS.SUCCESS);
      setCookie(null, GUIDE_COOKIE, true, { maxAge: 30 * 24 * 60 * 60 });
    } catch {
      setFormStatus(STATUS.ERROR);
    }
  };

  const handleDownloadUngatedGuide = () => {
    track('download_ungated_guide', {
      title: downloadTitle,
    });
    downloadFile();
  };

  return (
    <ThemeProvider theme={theme}>
      <Section data-testid="GuideMasthead" py={[8, null, null, 9]}>
        <Container>
          <Grid>
            <HeadingWrapper>
              {!!eyebrow?.length && (
                <Text variant="eyebrow" fontWeight="bold" color="tealDark">
                  {eyebrow}
                </Text>
              )}
              <Heading as="h1" variant={['h4', null, null, 'h3']} mt={5}>
                {heading}
              </Heading>
            </HeadingWrapper>
            {!!paragraph && (
              <ParagraphWrapper>
                <SimpleRichText content={paragraph?.json} />
              </ParagraphWrapper>
            )}
            <ResourceWrapper>
              <ResourceCard>
                <ImageWrapper>
                  <Image
                    src={image.url}
                    width={imageWidth}
                    height={imageHeight}
                    priority
                  />
                </ImageWrapper>
                {!!form && (
                  <>
                    {(formStatus === STATUS.IDLE || formStatus === STATUS.LOADING) && (
                      <ContentfulForm
                        mt={5}
                        formId={form.sys?.id}
                        onSubmit={handleSubmit}
                        title={form.title}
                        inputs={form.inputsCollection?.items}
                        disclosures={form.disclosures}
                        submitButtonText={form.submitButtonText}
                        submitButtonVariant={form.submitButtonVariant}
                        submitButtonProps={{ width: 1 }}
                      />
                    )}
                    {formStatus === STATUS.SUCCESS && (
                      <Box mt={5}>
                        <FormSuccessCard>
                          <FormCardIconWrapper>
                            <ThumbsUp size={24} color={theme.colors.blue100} />
                          </FormCardIconWrapper>
                          <Text variant="lead" fontWeight="bold">
                            {formSuccessCopy}
                          </Text>
                        </FormSuccessCard>
                        <Box mt={5}>
                          {downloadStatus !== 'loading' && (
                            <Button
                              data-testid="GatedGuideDownloadButton"
                              type="button"
                              onClick={downloadFile}
                              btnSize="lg"
                              variant="secondary"
                              width={1}
                            >
                              {downloadCtaLabel}
                            </Button>
                          )}
                          {downloadStatus === 'loading' && (
                            <Flex justifyContent="center">
                              <Loading />
                            </Flex>
                          )}
                        </Box>
                      </Box>
                    )}
                    {formStatus === STATUS.ERROR && (
                      <Box mt={5}>
                        <FormErrorCard>
                          <FormCardIconWrapper>
                            <Warning size={24} color={theme.colors.redError} />
                          </FormCardIconWrapper>
                          <Text variant="lead" color={theme.colors.redError}>
                            Something went wrong. Please try again!
                          </Text>
                        </FormErrorCard>
                      </Box>
                    )}
                  </>
                )}
                {!form && (
                  <Box mt={5}>
                    <Text variant="lead" fontWeight="bold">
                      {downloadHeading}
                    </Text>
                    <SimpleRichText mt={2} content={downloadParagraph?.json} />
                    <Box mt={5}>
                      {downloadStatus !== 'loading' && (
                        <Button
                          data-testid="UngatedGuideDownloadButton"
                          type="button"
                          onClick={handleDownloadUngatedGuide}
                          btnSize="lg"
                          variant="secondary"
                          width={1}
                        >
                          {downloadCtaLabel}
                        </Button>
                      )}
                      {downloadStatus === 'loading' && (
                        <Flex justifyContent="center">
                          <Loading />
                        </Flex>
                      )}
                    </Box>
                  </Box>
                )}
              </ResourceCard>
            </ResourceWrapper>
          </Grid>
        </Container>
      </Section>
    </ThemeProvider>
  );
};

GuideMasthead.propTypes = {
  ...guideMastheadProps,
};

GuideMasthead.defaultProps = {
  ...guideMastheadDefaultProps,
};

export default GuideMasthead;
